import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { clearOtpCache } from '../utility/otpHelper';

const OtpContext = createContext();

export const useOtpSettings = () => {
  const context = useContext(OtpContext);
  if (!context) {
    throw new Error('useOtpSettings must be used within OtpProvider');
  }
  return context;
};

export const OtpProvider = ({ children }) => {
  const [otpSettings, setOtpSettings] = useState({});
  const [loading, setLoading] = useState(true);

  // Simple toggle function
  const toggleOtp = async (componentName) => {
    const currentValue = otpSettings[componentName] ?? true;
    const newValue = !currentValue;

    try {
      await axios.put(`/api/otp-settings/${componentName}`, {
        is_otp_enabled: newValue
      }, { withCredentials: true });

      clearOtpCache(componentName);
      setOtpSettings(prev => ({
        ...prev,
        [componentName]: newValue
      }));
    } catch (err) {
      console.error('Toggle failed:', err);
      // Revert on error
      setOtpSettings(prev => ({
        ...prev,
        [componentName]: currentValue
      }));
    }
  };

  // Load settings
  const loadSettings = async () => {
    // Check if user is authenticated before making API call
    const roleID = localStorage.getItem('roleID');
    if (!roleID) {
      setLoading(false);
      return;
    }

    try {
      const instituteId = localStorage.getItem('instituteId');
      const params = instituteId ? { institute_id: instituteId } : {};
      console.log('Loading OTP settings for institute:', instituteId, 'params:', params);
      const response = await axios.get('/api/otp-settings', { 
        params,
        withCredentials: true 
      });
      const settingsObj = {};
      response.data.forEach(setting => {
        settingsObj[setting.component_name] = setting.is_otp_enabled;
      });
      console.log('Loaded OTP settings:', settingsObj);
      setOtpSettings(settingsObj);
    } catch (err) {
      console.error('Failed to load settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const reloadSettings = () => {
    loadSettings();
  };

  useEffect(() => {
    loadSettings();
  }, []);

  // Listen for localStorage changes and custom events to reload settings when instituteId changes
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'instituteId') {
        loadSettings();
      }
    };
    
    const handleInstituteChange = () => {
      loadSettings();
    };
    
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('instituteChanged', handleInstituteChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('instituteChanged', handleInstituteChange);
    };
  }, []);

  const value = {
    otpSettings,
    loading,
    toggleOtp,
    reloadSettings,
    isOtpEnabled: (componentName) => otpSettings[componentName] ?? true
  };

  return <OtpContext.Provider value={value}>{children}</OtpContext.Provider>;
};
